// src/components/layout/PageLeaveGuardContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    allowNextPageUnload,
    consumeAllowNextUnload,
    dismissLeavePromptForever,
    getDirtyReasons,
    isSameAppPath,
    shouldPromptOnLeave,
    shouldUseNativeUnloadPrompt
} from '../../utils/pageLeaveGuard';
import PageLeaveConfirmModal from './PageLeaveConfirmModal';

const PageLeaveGuardContext = createContext({
    navigate: (href) => window.location.assign(href)
});

function goTo(href) {
    allowNextPageUnload();
    window.location.assign(href);
}

export function PageLeaveGuardProvider({ children }) {
    const [pendingHref, setPendingHref] = useState(null);
    const [reasons, setReasons] = useState([]);

    const navigate = useCallback((href) => {
        if (!href || isSameAppPath(href)) return;
        if (!shouldPromptOnLeave()) {
            goTo(href);
            return;
        }
        setReasons(getDirtyReasons());
        setPendingHref(href);
    }, []);

    const handleStay = useCallback(() => {
        setPendingHref(null);
        setReasons([]);
    }, []);

    const handleContinue = useCallback((dontShowAgain = false) => {
        if (dontShowAgain) dismissLeavePromptForever();
        const href = pendingHref;
        setPendingHref(null);
        setReasons([]);
        if (href) goTo(href);
    }, [pendingHref]);

    useEffect(() => {
        const onBeforeUnload = (event) => {
            if (consumeAllowNextUnload() || !shouldUseNativeUnloadPrompt()) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    const value = useMemo(() => ({ navigate }), [navigate]);

    return (
        <PageLeaveGuardContext.Provider value={value}>
            {children}
            <PageLeaveConfirmModal
                show={Boolean(pendingHref)}
                reasons={reasons}
                onStay={handleStay}
                onContinue={handleContinue}
            />
        </PageLeaveGuardContext.Provider>
    );
}

export function usePageLeaveGuard() {
    return useContext(PageLeaveGuardContext);
}

export default PageLeaveGuardContext;
